import { TicketType } from '../types/ticket';

export interface TicketTypeMeta {
	// Short noun for chips, grid cells, and other dense surfaces (≤ 8 chars).
	shortLabel: string;
	// Primary user-facing label for buttons, menu items, and card headlines.
	label: string;
	// Lowercase plural for count contexts (e.g. "3 records this month").
	pluralLabel: string;
	// One-line description of the physical action the operator takes.
	description: string;
	// Verb-leading CTA text for primary action buttons.
	buttonText: string;
	textColor: string;
	buttonColor:
		| 'success'
		| 'primary'
		| 'secondary'
		| 'error'
		| 'info'
		| 'warning';
	hexColor: string;
}

export const defaultTicketTypeMap: Record<TicketType, TicketTypeMeta> = {
	log: {
		shortLabel: 'Record',
		label: 'Record only',
		pluralLabel: 'records',
		description: 'No action on the vehicle',
		buttonText: 'Record violation',
		textColor: 'secondary.main',
		buttonColor: 'secondary',
		hexColor: '#C20EC2',
	},
	logAndWarn: {
		shortLabel: 'Warning',
		label: 'Issue warning',
		pluralLabel: 'warnings',
		description: 'Place a warning notice on the windshield',
		buttonText: 'Issue warning',
		textColor: 'warning.main',
		buttonColor: 'warning',
		hexColor: '#FFA726',
	},
	tow: {
		shortLabel: 'Tow',
		label: 'Authorize tow',
		pluralLabel: 'tows',
		description: 'Call the tow truck',
		buttonText: 'Authorize tow',
		textColor: 'error.main',
		buttonColor: 'error',
		hexColor: '#EF5350',
	},
};

const fallbackMeta: TicketTypeMeta = {
	shortLabel: 'Unknown',
	label: 'Unknown',
	pluralLabel: 'unknown',
	description: '',
	buttonText: 'Unknown',
	textColor: 'text.primary',
	buttonColor: 'primary',
	hexColor: '#000000',
};

export function getTicketTypeMeta(ticketType: string): TicketTypeMeta {
	return defaultTicketTypeMap[ticketType as TicketType] ?? fallbackMeta;
}

// --- Per-tenant terminology override -------------------------------------
//
// Clients can rename the three action levels via the `canpark / ticket_terminology`
// instance setting. Only the *wording* changes — colors stay tied to severity. The
// override is intentionally minimal: a noun (`label`) and a verb phrase (`actionText`)
// per type, because those are the only two linguistically independent forms; the five
// meta text fields are derived from them (see `mergeTerminology`). Any omitted field
// falls back to the built-in default.

export interface TicketTypeTerminology {
	// Noun form → drives `shortLabel` (chips) and `pluralLabel` (counts/legends).
	label?: string;
	// Verb phrase → drives `label` (menus/next-steps) and `buttonText` (CTAs).
	actionText?: string;
	// One-line action hint → drives `description`.
	description?: string;
}

export type TerminologyOverride = Partial<Record<TicketType, TicketTypeTerminology>>;

// Naive English pluralizer — enough for the short nouns used here
// (Permit→permits, Warning→warnings, Ticket→tickets, Box→boxes, Entry→entries).
function pluralize(word: string): string {
	if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
	if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
	return `${word}s`;
}

const TICKET_TYPES: TicketType[] = ['log', 'logAndWarn', 'tow'];

// Merge a (partial) tenant override over the defaults, deriving every meta text field
// from `label`/`actionText`/`description`. Colors are always taken from the default.
export function mergeTerminology(
	override: TerminologyOverride | null | undefined,
): Record<TicketType, TicketTypeMeta> {
	if (!override) return defaultTicketTypeMap;

	const merged = {} as Record<TicketType, TicketTypeMeta>;
	for (const type of TICKET_TYPES) {
		const base = defaultTicketTypeMap[type];
		const o = override[type];
		if (!o || typeof o !== 'object') {
			merged[type] = base;
			continue;
		}
		const label = o.label?.trim();
		const actionText = o.actionText?.trim();
		const description = o.description?.trim();
		merged[type] = {
			...base,
			shortLabel: label || base.shortLabel,
			pluralLabel: label ? pluralize(label.toLowerCase()) : base.pluralLabel,
			label: actionText || base.label,
			buttonText: actionText || base.buttonText,
			description: description || base.description,
		};
	}
	return merged;
}
